import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ChevronLeft, Phone, Clock, Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AutoTextarea } from '@/components/ui/auto-textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { fireEvent } from '@/lib/analytics';
import { validateEmail } from '@/utils/contactFormUtils';
import { toast } from '@/hooks/use-toast';

const SUBJECT_OPTIONS = [
  { value: 'purchase', label: 'Purchase Question' },
  { value: 'leasing', label: 'Leasing Question' },
  { value: 'technical', label: 'Technical Issue' },
  { value: 'account', label: 'Account Support' },
  { value: 'other', label: 'Other' },
];

export default function SupportContact() {
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    subject: 'purchase',
    domain: '',
    message: '',
    honeypot: '',
  });
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [status, setStatus] = React.useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = React.useState('');

  React.useEffect(() => {
    fireEvent('support_view', { route: 'contact' });
  }, []);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!validateEmail(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    if (formData.message.length > 5000)
      newErrors.message = 'Message is too long (max 5000 characters)';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot check
    if (formData.honeypot) {
      setStatus('success');
      return;
    }

    if (!validateForm()) return;

    setStatus('submitting');
    setErrorMessage('');
    setErrors({});

    try {
      const subjectLabel =
        SUBJECT_OPTIONS.find((opt) => opt.value === formData.subject)?.label || 'Support Request';
      
      // Compose subject with domain if provided
      const emailSubject = `[Support] ${subjectLabel}${formData.domain ? ` - ${formData.domain}` : ''}`;
      
      // Include domain in message if provided (edge function will format the email)
      const messageWithDomain = formData.domain
        ? `Domain: ${formData.domain}\n\n${formData.message}`
        : formData.message;

      const { data, error } = await supabase.functions.invoke('contact-submit', {
        body: {
          name: formData.name,
          email: formData.email,
          subject: emailSubject,
          message: messageWithDomain,
        },
      });

      // Check response structure first (data may contain error response even when error is set)
      if (data && !data?.ok) {
        if (data?.code === 'VALIDATION_ERROR' && data?.fields) {
          // Server validation errors
          setErrors((prev) => ({ ...prev, ...data.fields }));
          setStatus('error');
          setErrorMessage('Please correct the highlighted fields.');
          toast({
            title: 'Check your input',
            description: 'Please correct the highlighted fields.',
            variant: 'destructive',
          });
          fireEvent('support_contact_submit', { ok: false });
          return;
        }

        if (data?.code === 'RATE_LIMIT') {
          setStatus('error');
          setErrorMessage('Too many attempts, please wait.');
          toast({
            title: 'Rate limited',
            description: 'Too many attempts, please wait.',
            variant: 'destructive',
          });
          fireEvent('support_contact_submit', { ok: false });
          return;
        }

        if (data?.code === 'CONFIG_ERROR') {
          setStatus('error');
          setErrorMessage('Service temporarily unavailable. Please try again later or email support@attaindomains.com directly.');
          toast({
            title: 'Service unavailable',
            description: 'Please try again later',
            variant: 'destructive',
          });
          fireEvent('support_contact_submit', { ok: false, code: 'CONFIG_ERROR' });
          return;
        }

        if (data?.code === 'MAIL_SEND_FAILED') {
          setStatus('error');
          setErrorMessage('Unable to send message. Please try again or email support@attaindomains.com directly.');
          toast({
            title: 'Send failed',
            description: 'Please try again',
            variant: 'destructive',
          });
          fireEvent('support_contact_submit', { ok: false, code: 'MAIL_SEND_FAILED' });
          return;
        }

        // Generic error
        throw new Error(data?.message || 'Failed to send message');
      }

      // Handle errors - check error.context.body first (Supabase puts response body here for non-2xx)
      if (error) {
        console.error('[SupportContact] Edge function error:', error);
        
        // Try to extract error response from error.context.body
        let errorData: any = null;
        if (error.context?.body) {
          try {
            errorData = typeof error.context.body === 'string' 
              ? JSON.parse(error.context.body) 
              : error.context.body;
          } catch (e) {
            // If parsing fails, errorData stays null
          }
        }
        
        // If we have structured error data, handle it
        if (errorData) {
          if (errorData.code === 'VALIDATION_ERROR' && errorData.fields) {
            setErrors((prev) => ({ ...prev, ...errorData.fields }));
            setStatus('error');
            setErrorMessage('Please correct the highlighted fields.');
            toast({
              title: 'Check your input',
              description: 'Please correct the highlighted fields.',
              variant: 'destructive',
            });
            fireEvent('support_contact_submit', { ok: false });
            return;
          }
          
          if (errorData.code === 'RATE_LIMIT') {
            setStatus('error');
            setErrorMessage('Too many attempts, please wait.');
            toast({
              title: 'Rate limited',
              description: 'Too many attempts, please wait.',
              variant: 'destructive',
            });
            fireEvent('support_contact_submit', { ok: false });
            return;
          }
          
          if (errorData.code === 'CONFIG_ERROR') {
            setStatus('error');
            setErrorMessage('Service temporarily unavailable. Please try again later or email support@attaindomains.com directly.');
            toast({
              title: 'Service unavailable',
              description: 'Please try again later',
              variant: 'destructive',
            });
            fireEvent('support_contact_submit', { ok: false, code: 'CONFIG_ERROR' });
            return;
          }
          
          if (errorData.code === 'MAIL_SEND_FAILED') {
            setStatus('error');
            setErrorMessage('Unable to send message. Please try again or email support@attaindomains.com directly.');
            toast({
              title: 'Send failed',
              description: 'Please try again',
              variant: 'destructive',
            });
            fireEvent('support_contact_submit', { ok: false, code: 'MAIL_SEND_FAILED' });
            return;
          }
        }
        
        // Fallback: show error message if available, otherwise generic network error
        const errorMessage = errorData?.message || error.message || 'Network error. Please check your connection and try again.';
        setStatus('error');
        setErrorMessage(errorMessage);
        toast({
          title: errorData ? 'Error' : 'Connection error',
          description: errorMessage,
          variant: 'destructive',
        });
        fireEvent('support_contact_submit', { ok: false, code: errorData?.code || 'NETWORK_ERROR' });
        return;
      }

      // Success
      fireEvent('support_contact_submit', { ok: true });
      setStatus('success');
      toast({
        title: 'Message sent successfully!',
        description: "Thanks! I got your message. I'll reply soon.",
      });

      // Redirect to success page after short delay
      setTimeout(() => {
        window.location.href = '/support/contact/success';
      }, 1500);
    } catch (error) {
      console.error('[SupportContact] Submit error:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to send message. Please try again.';
      setErrorMessage(errorMessage);
      setStatus('error');
      fireEvent('support_contact_submit', { ok: false });
      toast({
        title: 'Failed to send message',
        description: 'Please try again or contact me directly.',
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Support - Attain Domains</title>
        <meta
          name="description"
          content="Get in touch with Attain Domains support team. We respond within 1 business day."
        />
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div className="min-h-screen bg-background py-12">
        <div className="max-w-4xl mx-auto px-6">
          {/* Back link */}
          <Link
            to="/support"
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Support
          </Link>

          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Contact Support</h1>
            <p className="text-lg text-muted-foreground">
              We typically respond within 1 business day
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Form */}
            <Card className="lg:col-span-2 p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot */}
                <input
                  type="text"
                  name="website"
                  value={formData.honeypot}
                  onChange={(e) => setFormData((prev) => ({ ...prev, honeypot: e.target.value }))}
                  style={{ display: 'none' }}
                  tabIndex={-1}
                  autoComplete="off"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder="John Doe"
                      className={errors.name ? 'border-destructive' : ''}
                    />
                    {errors.name && (
                      <p className="text-sm text-destructive flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.name}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                      placeholder="john@example.com"
                      className={errors.email ? 'border-destructive' : ''}
                    />
                    {errors.email && (
                      <p className="text-sm text-destructive flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.email}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="subject">Subject *</Label>
                  <Select
                    value={formData.subject}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, subject: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SUBJECT_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="domain">Domain (optional)</Label>
                  <Input
                    id="domain"
                    value={formData.domain}
                    onChange={(e) => setFormData((prev) => ({ ...prev, domain: e.target.value }))}
                    placeholder="example.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Message *</Label>
                  <AutoTextarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData((prev) => ({ ...prev, message: e.target.value }))}
                    placeholder="Tell us how we can help..."
                    rows={6}
                    maxLength={5000}
                    className={errors.message ? 'border-destructive' : ''}
                  />
                  {errors.message && (
                    <p className="text-sm text-destructive flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.message}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {formData.message.length} / 5000 characters
                  </p>
                </div>

                {status === 'error' && errorMessage && (
                  <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <p className="text-sm">{errorMessage}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  size="lg"
                  disabled={status === 'submitting' || status === 'success'}
                  className="w-full"
                >
                  {status === 'success' ? (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Message Sent!
                    </>
                  ) : status === 'submitting' ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </Card>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold">Response Time</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Our support team typically responds within 1 business day. For urgent inquiries,
                  please include "URGENT" in your subject.
                </p>
              </Card>

              <Card className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Phone className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold">Business Hours</h3>
                </div>
                <div className="text-sm space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Mon-Fri</span>
                    <span className="font-medium">9 AM - 5 PM EST</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Sat</span>
                    <span className="font-medium">10 AM - 2 PM EST</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Sun</span>
                    <span className="font-medium">Closed</span>
                  </div>
                </div>
              </Card>

              <Card className="p-6 bg-primary/5 border-primary/20">
                <h3 className="font-semibold mb-2">Quick FAQ</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Check our FAQ section for instant answers
                </p>
                <Link to="/support">
                  <Button variant="outline" size="sm" className="w-full">
                    Browse FAQs
                  </Button>
                </Link>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
